import { getProjectGitSettingsFromStorage } from '../../../../shared/lib/storageUtils';
import { getProject } from '../../projects/api/projectsApi';
import type { ProjectGitSettings } from '../../projects/types';

/** repo, branch, and token must all be non-empty (trimmed). */
export function isCompleteGithubTriple(
    g: { repo_url?: string; branch?: string; token?: string } | null | undefined
): boolean {
    if (!g || typeof g !== 'object') return false;
    return (
        String(g.repo_url ?? '').trim().length > 0 &&
        String(g.branch ?? '').trim().length > 0 &&
        String(g.token ?? '').trim().length > 0
    );
}

/**
 * Effective GitHub triple for discovery: project-scoped localStorage first, then global `githubSettings`.
 */
export function readGithubSettingsFromStorage(
    projectId: string | number | null | undefined
): ProjectGitSettings | null {
    const raw = getProjectGitSettingsFromStorage(projectId, { includeGlobalFallback: true });
    if (isCompleteGithubTriple(raw)) {
        return {
            repo_url: String(raw!.repo_url).trim(),
            branch: String(raw!.branch).trim(),
            token: String(raw!.token).trim(),
        };
    }
    return null;
}

function normalizeProjectGit(g: ProjectGitSettings | null | undefined): ProjectGitSettings | null {
    if (!g || typeof g !== 'object') return null;
    const o = g as Record<string, unknown>;
    const repo = String(g.repo_url ?? o.repository_path ?? '').trim();
    const branch = String(g.branch ?? o.branch_or_feature ?? '').trim();
    const token = String(g.token ?? o.github_token ?? '').trim();
    const mapped = { repo_url: repo, branch, token };
    if (!isCompleteGithubTriple(mapped)) return null;
    return mapped;
}

/**
 * Complete triple: project-scoped `githubSettings` first, then global storage, then project `git_settings` from the API.
 */
export async function readGithubSettingsWithServerFallback(
    projectId: string | number | null | undefined
): Promise<ProjectGitSettings | null> {
    const fromStorage = readGithubSettingsFromStorage(projectId);
    if (fromStorage) return fromStorage;
    if (projectId == null || String(projectId).trim() === '') {
        return null;
    }
    try {
        const p = await getProject(projectId);
        return normalizeProjectGit(p.git_settings ?? undefined);
    } catch {
        return null;
    }
}

/**
 * True when the user may proceed: complete triple in localStorage (project then global), else the same from API `git_settings`.
 */
export async function isGithubReadyForDiscoveryProceed(
    projectId: string | number | null | undefined
): Promise<boolean> {
    return (await readGithubSettingsWithServerFallback(projectId)) != null;
}

export const DISCOVERY_GITHUB_GATE_MESSAGE =
    'GitHub repository, branch, and token are required. Open Project settings → Git, save your repo and token, then try Proceed again.';
